import { z } from 'zod';

/**
 * The social calls of the game server, over HTTP with the player's own token (`Authorization: Bearer`):
 * who of the friends is here, and an invitation to the private room the player is in. Friendships live
 * in the database; the server only knows who is connected and where, which is what these answer.
 */

/** Where a friend is: not here, here, at a table that is still gathering, or playing. */
export const PresenceStateSchema = z.enum(['offline', 'online', 'lobby', 'playing']);
export type PresenceState = z.infer<typeof PresenceStateSchema>;

export const FriendPresenceSchema = z.strictObject({
  state: PresenceStateSchema,
  /** The code of the private room a friend is gathering in: they may be joined by it. */
  code: z.string().max(32).optional(),
});
export type FriendPresence = z.infer<typeof FriendPresenceSchema>;

export const InviteSchema = z.strictObject({
  id: z.string().min(1).max(64),
  /** `nickname#tag` of who invites. */
  from: z.string().min(1).max(40),
  /** The private room to join. */
  code: z.string().min(1).max(32),
  at: z.number().int(),
});
export type Invite = z.infer<typeof InviteSchema>;

export const PresenceResponseSchema = z.strictObject({
  friends: z.record(z.string(), FriendPresenceSchema),
  /** What was sent to the player since the last call and has not expired. */
  invites: z.array(InviteSchema).max(20),
});
export type PresenceResponse = z.infer<typeof PresenceResponseSchema>;

/** Why an invitation was not sent. */
export const InviteErrorSchema = z.enum([
  'notInRoom',
  'notFriends',
  'offline',
  'rateLimited',
  'unauthorized',
  'featureOff',
  'badRequest',
]);
export type InviteError = z.infer<typeof InviteErrorSchema>;

export const InviteResponseSchema = z.union([
  z.strictObject({ ok: z.literal(true) }),
  z.strictObject({ ok: z.literal(false), error: InviteErrorSchema }),
]);
export type InviteResponse = z.infer<typeof InviteResponseSchema>;

/** How often a player says they are here, in milliseconds (a friend counts them online for twice this and a little more). */
export const PRESENCE_BEAT_MS = 20_000;
/** How long an invitation can be answered. */
export const INVITE_TTL_MS = 5 * 60_000;
/** The most friends asked about in one call. */
export const PRESENCE_MAX_IDS = 100;
